import { Fragment, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, PowerOff, Copy, Stethoscope, CheckCircle2, AlertTriangle, XCircle, HelpCircle } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'
import type { Tenant } from '@wsfull/shared'

type ClientForm = {
  name: string; cnpj: string; email: string; phone: string
  ownProduction: boolean; fulfillmentClient: boolean; industryClient: boolean
}
const empty: ClientForm = {
  name: '', cnpj: '', email: '', phone: '',
  ownProduction: false, fulfillmentClient: true, industryClient: false,
}

// Diagnóstico da rotina do cliente: a pergunta "está tudo certo com este
// cliente?" não se responde abrindo sete telas e comparando números de cabeça —
// é assim que uma ponta fica solta por semanas.
type Checagem = {
  chave: string; area: string; titulo: string
  estado: 'ok' | 'atencao' | 'erro' | 'indefinido'
  numero: number | null; detalhe: string; oQueFazer: string | null
}
type Diagnostico = {
  cliente: { id: string; nome: string; cnpj: string; ativo: boolean }
  geradoEm: string
  resumo: { ok: number; atencao: number; erro: number; indefinido: number }
  checagens: Checagem[]
}

const ICONE = {
  ok: <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />,
  atencao: <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />,
  erro: <XCircle className="h-4 w-4 shrink-0 text-red-600" />,
  indefinido: <HelpCircle className="h-4 w-4 shrink-0 text-gray-400" />,
}

function PainelDiagnostico({ tenantId }: { tenantId: string }) {
  const { data, isLoading, error } = useQuery<Diagnostico>({
    queryKey: ['diagnostico', tenantId],
    queryFn: async () => (await api.get(`/clients/${tenantId}/diagnostico`)).data,
  })
  if (isLoading) return <p className="px-5 py-4 text-sm text-gray-400">Conferindo a rotina deste cliente…</p>
  if (error || !data) {
    const err = error as { response?: { data?: { title?: string } } } | null
    return <p className="px-5 py-4 text-sm text-red-600">{err?.response?.data?.title ?? 'Falha ao rodar o diagnóstico'}</p>
  }
  // Ordem: o que está quebrado primeiro. A lista existe para ser resolvida de
  // cima para baixo, não para ser lida inteira.
  const peso = { erro: 0, atencao: 1, indefinido: 2, ok: 3 }
  const linhas = [...data.checagens].sort((a, b) => peso[a.estado] - peso[b.estado])
  return (
    <div className="space-y-3 px-5 py-4">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="font-semibold text-gray-900">{data.cliente.nome}</span>
        <span className="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">{data.resumo.ok} ok</span>
        {data.resumo.atencao > 0 && <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">{data.resumo.atencao} atenção</span>}
        {data.resumo.erro > 0 && <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">{data.resumo.erro} erro</span>}
        {data.resumo.indefinido > 0 && <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{data.resumo.indefinido} conferir</span>}
        <span className="text-xs text-gray-400">gerado em {new Date(data.geradoEm).toLocaleString('pt-BR')}</span>
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-100">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-100 bg-gray-50 text-xs text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Área</th>
              <th className="px-3 py-2 text-left">Checagem</th>
              <th className="px-3 py-2 text-right">Nº</th>
              <th className="px-3 py-2 text-left">Situação</th>
              <th className="px-3 py-2 text-left">O que fazer</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {linhas.map(ch => (
              <tr key={ch.chave} className={ch.estado === 'erro' ? 'bg-red-50/50' : ch.estado === 'atencao' ? 'bg-amber-50/40' : ''}>
                <td className="px-3 py-2 text-xs text-gray-500">{ch.area}</td>
                <td className="px-3 py-2">
                  <span className="flex items-center gap-2 font-medium text-gray-800">{ICONE[ch.estado]} {ch.titulo}</span>
                </td>
                <td className="px-3 py-2 text-right font-semibold text-gray-800">{ch.numero ?? '—'}</td>
                <td className="px-3 py-2 text-xs text-gray-600">{ch.detalhe}</td>
                <td className="px-3 py-2 text-xs text-blue-700">{ch.oQueFazer ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function ClientsPage() {
  const qc = useQueryClient()
  const [diagFor, setDiagFor] = useState<string | null>(null)
  const [modal, setModal] = useState<{ open: boolean; client?: Tenant }>({ open: false })
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const form_state = useState<ClientForm>(empty)
  const [form, setForm] = form_state

  const { data: clients = [], isLoading } = useQuery<Tenant[]>({
    queryKey: ['clients'],
    queryFn: async () => (await api.get('/clients')).data,
  })

  const openCreate = () => { setForm(empty); setModal({ open: true }) }
  const openEdit   = (c: Tenant) => {
    setForm({
      name: c.name, cnpj: c.cnpj, email: c.email ?? '', phone: c.phone ?? '',
      ownProduction: c.ownProduction,
      fulfillmentClient: c.fulfillmentClient ?? true,
      industryClient: c.industryClient ?? false,
    })
    setModal({ open: true, client: c })
  }
  const close = () => setModal({ open: false })

  const save = useMutation({
    mutationFn: async () => {
      const body = { ...form, email: form.email || undefined, phone: form.phone || undefined }
      if (modal.client) {
        const { cnpj: _cnpj, ...upd } = body
        await api.patch(`/clients/${modal.client.id}`, upd)
      } else {
        await api.post('/clients', body)
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['clients'] }); close() },
  })

  const deactivate = useMutation({
    mutationFn: (id: string) => api.delete(`/clients/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  })

  const f = (k: keyof ClientForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(p => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }))

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Clientes</h2>
          <p className="text-sm text-gray-500">{clients.length} cliente(s) cadastrado(s)</p>
        </div>
        <Button size="sm" onClick={openCreate}><Plus className="h-4 w-4" /> Novo cliente</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
          ) : clients.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Nenhum cliente cadastrado</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Nome</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">CNPJ</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Email</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Prod. própria</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Status</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {clients.map((c) => (
                    <Fragment key={c.id}>
                    <tr className={`hover:bg-gray-50 ${diagFor === c.id ? 'bg-blue-50/60' : ''}`}>
                      <td className="px-5 py-3 font-medium text-gray-900">{c.name}</td>
                      <td className="px-5 py-3 font-mono text-gray-600">
                        {c.cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')}
                      </td>
                      <td className="px-5 py-3 text-gray-600">{c.email ?? '—'}</td>
                      <td className="px-5 py-3">
                        <Badge variant={c.ownProduction ? 'info' : 'default'}>{c.ownProduction ? 'Sim' : 'Não'}</Badge>
                      </td>
                      <td className="px-5 py-3">
                        <Badge variant={c.active ? 'success' : 'danger'}>{c.active ? 'Ativo' : 'Inativo'}</Badge>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" title="Copiar ID (tenantId) — use na integração/API"
                            onClick={() => { navigator.clipboard?.writeText(c.id); setCopiedId(c.id); setTimeout(() => setCopiedId(null), 1500) }}>
                            {copiedId === c.id ? <span className="text-xs font-medium text-green-600">copiado!</span> : <Copy className="h-4 w-4" />}
                          </Button>
                          <Button variant="ghost" size="sm" title="Diagnóstico da rotina deste cliente"
                            onClick={() => setDiagFor(d => (d === c.id ? null : c.id))}>
                            <Stethoscope className={`h-4 w-4 ${diagFor === c.id ? 'text-blue-600' : ''}`} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEdit(c)}><Pencil className="h-4 w-4" /></Button>
                          {c.active && (
                            <Button variant="ghost" size="sm" onClick={() => deactivate.mutate(c.id)}>
                              <PowerOff className="h-4 w-4 text-red-400" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {diagFor === c.id && (
                      <tr className="bg-blue-50/30">
                        <td colSpan={6} className="p-0"><PainelDiagnostico tenantId={c.id} /></td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Modal title={modal.client ? 'Editar cliente' : 'Novo cliente'} open={modal.open} onClose={close}>
        <div className="space-y-4">
          <Input label="Nome *" value={form.name} onChange={f('name')} placeholder="Empresa Ltda" />
          {!modal.client && (
            <Input label="CNPJ * (só números)" value={form.cnpj} onChange={f('cnpj')} placeholder="00000000000000" maxLength={14} />
          )}
          <Input label="Email" type="email" value={form.email} onChange={f('email')} placeholder="contato@empresa.com.br" />
          <Input label="Telefone" value={form.phone} onChange={f('phone')} placeholder="(11) 99999-9999" />
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" className="h-4 w-4 rounded" checked={form.ownProduction} onChange={f('ownProduction')} />
            <span className="text-sm text-gray-700">Produção própria (isento de taxa de armazenagem)</span>
          </label>

          {/* Fulfillment e indústria são conjuntos distintos: o mesmo cliente
              pode ser um, outro ou os dois. Sem a flag de indústria ele não
              aparece em nenhuma tela do módulo. */}
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">O que este cliente usa</p>
            <label className="flex cursor-pointer items-center gap-3 py-1">
              <input type="checkbox" className="h-4 w-4 rounded" checked={form.fulfillmentClient} onChange={f('fulfillmentClient')} />
              <span className="text-sm text-gray-700">Fulfillment — estoque, pedidos e expedição</span>
            </label>
            <label className="flex cursor-pointer items-center gap-3 py-1">
              <input type="checkbox" className="h-4 w-4 rounded" checked={form.industryClient} onChange={f('industryClient')} />
              <span className="text-sm text-gray-700">Indústria — remessa de material, ficha técnica e ordens de produção</span>
            </label>
          </div>
          {save.error && <p className="text-sm text-red-600">{(save.error as any)?.response?.data?.title ?? 'Erro ao salvar'}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={close}>Cancelar</Button>
            <Button loading={save.isPending} disabled={!form.name || (!modal.client && form.cnpj.length !== 14)} onClick={() => save.mutate()}>
              {modal.client ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
